/**
 * `agentWritesApi` — RTK Query slice for cypher-write apply/reject and
 * resume-dispatch against the ontology api-service (`/api/ontology/
 * agent-writes/*`).
 *
 * Split off from the legacy `ontologyApi` catch-all so agent write
 * traffic owns its own reducer path. Uses `ontologyBaseQuery` (same
 * auth + branch headers as the proxy-backed slices); endpoint URLs are
 * absolute against `getOntologyServiceBaseUrl()` so RTK does not
 * re-root them under `/api/v1/ontology`.
 *
 * Hooks are exported for `CypherWriteProposalPanel`; resume helpers in
 * `cypher-write-resume.ts` call `.endpoints.*.initiate()` directly.
 */

import { createApi } from '@reduxjs/toolkit/query/react'

import { getOntologyServiceBaseUrl } from '../../../config'
import { ontologyBaseQuery } from '../../core'

const ONTOLOGY_SERVICE_URL = getOntologyServiceBaseUrl()

export interface ApplyCypherWriteArgs {
  toolUseId: string
  graphAffecting: boolean
  /** Optional job-branch override for ingest Conversation applies. */
  branchId?: string
}

export const agentWritesApi = createApi({
  reducerPath: 'agentWritesApi',
  baseQuery: ontologyBaseQuery,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    applyCypherWrite: builder.mutation<CypherWriteApplyResult, ApplyCypherWriteArgs>({
      query: ({ toolUseId, branchId }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/agent-writes/by-tool-use/${encodeURIComponent(toolUseId)}/apply`,
        method: 'POST',
        ...(branchId
          ? { headers: { 'X-Ontology-Branch': branchId } as Record<string, string> }
          : {})
      })
    }),
    rejectCypherWrite: builder.mutation<
      CypherWriteRejectResult,
      { toolUseId: string; reason?: string }
    >({
      query: ({ toolUseId, reason }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/agent-writes/by-tool-use/${encodeURIComponent(toolUseId)}/reject`,
        method: 'POST',
        body: { reason: reason ?? 'rejected by user' }
      })
    }),
    revertTagWrite: builder.mutation<TagWriteRevertResult, { eventId: string }>({
      query: ({ eventId }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/writeback/events/${encodeURIComponent(eventId)}:revert`,
        method: 'POST'
      })
    }),
    claimCypherWriteResumeDispatch: builder.mutation<
      ClaimResumeDispatchResult,
      { planId: string; conversationId: string }
    >({
      query: ({ planId, conversationId }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/agent-writes/by-plan/${encodeURIComponent(planId)}/claim-resume-dispatch?conversation_id=${encodeURIComponent(conversationId)}`,
        method: 'POST'
      })
    }),
    listSettledUnresumedCypherPlans: builder.query<
      SettledUnresumedPlan[],
      { conversationId: string }
    >({
      query: ({ conversationId }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/agent-writes/settled-unresumed?conversation_id=${encodeURIComponent(conversationId)}`,
        method: 'GET'
      }),
      transformResponse: (response: { plans?: SettledUnresumedPlan[] }) => response.plans ?? []
    })
  })
})

export interface CypherWriteApplyResult {
  retryable?: boolean
  ok: boolean
  writeId?: string
  state?: string
  counts?: {
    nodesCreated?: number
    nodesModified?: number
    nodesDeleted?: number
    relationshipsCreated?: number
    relationshipsDeleted?: number
    totalOperations?: number
  }
  /** Action-specific output for non-graph actions (e.g. created ServiceNow incident). */
  result?: Record<string, unknown>
  error?: string
}

export interface CypherWriteRejectResult {
  ok: boolean
  writeId?: string
  state?: string
  reason?: string
  error?: string
}

export interface TagWriteRevertResult {
  event_id: string
  outcome: string
  target_id: string
  prior_value?: unknown
  replay?: boolean
}

export interface ClaimResumeDispatchResult {
  claimed: boolean
  rowCount: number
  dispatchedAt?: string
  alreadyDispatchedAt?: string
}

export interface SettledUnresumedPlan {
  planId: string
  conversationId: string
  planSize: number
  lastSettledAt: string
}

export const {
  useApplyCypherWriteMutation,
  useRejectCypherWriteMutation,
  useRevertTagWriteMutation
} = agentWritesApi
